import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldAlert, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const MIN_REASON = 10;

const META = {
  verified: { label: 'ABR VERIFIED', color: '#1D9E75' },
  verified_pending: { label: 'VERIFIED PENDING — ABR UNAVAILABLE AT BIND', color: '#E8A020' },
  unverified_no_key: { label: 'CHECKSUM VERIFIED — ABR LOOKUPS DEFERRED', color: '#5C6B7A' },
  failed_checksum: { label: 'INVALID ABN — CHECKSUM FAILED', color: '#E24B4A' },
  failed_not_found: { label: 'ABN NOT FOUND ON ABR', color: '#E24B4A' },
  failed_cancelled: { label: 'ABN CANCELLED / DEREGISTERED', color: '#E24B4A' },
  failed_name_mismatch: { label: 'ABR LEGAL NAME MISMATCH', color: '#E24B4A' },
  missing_abn: { label: 'NO ABN ON SUBMISSION', color: '#E24B4A' },
  override: { label: 'OVERRIDE RECORDED — BIND PERMITTED', color: '#7C5CBF' },
  not_checked: { label: 'ABN OPTIONAL — NOT CHECKED AT BIND', color: '#5C6B7A' },
  not_applicable_individual: { label: 'PRIVATE INDIVIDUAL — NO ABN REQUIRED', color: '#5C6B7A' },
  not_applicable_nz: { label: 'NZ ENTITY — ABR NOT APPLICABLE', color: '#5C6B7A' },
};

function OverrideForm({ api, submissionId, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const short = reason.trim().length < MIN_REASON;
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="abr-override-toggle"
        className="text-[10px] underline mt-1" style={{ color: '#5C6B7A' }}>
        Override with an audited reason (underwriter judgement)
      </button>
    );
  }
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/submissions/${submissionId}/abr-override`, { reason });
      toast.success('ABR override recorded — audited against your name');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Override refused');
    }
    setSaving(false);
  };
  return (
    <div className="mt-2 p-3 rounded-sm space-y-2" style={{ background: 'rgba(124,92,191,0.05)', border: '1px solid rgba(124,92,191,0.25)' }} data-testid="abr-override-form">
      <p className="text-[10px]" style={{ color: '#7C5CBF' }}>
        The override is recorded against your name in the audit trail and prints with the bind record. It clears this specific failure only — a different ABR failure re-blocks the bind.
      </p>
      <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} data-testid="abr-override-reason"
        placeholder={`Why is binding without ABR verification right here? (minimum ${MIN_REASON} characters)`}
        className="w-full px-2 py-1.5 border border-input rounded-sm text-xs bg-white" />
      <div className="flex items-center justify-between">
        <span className="text-[10px]" style={{ color: short ? '#E24B4A' : '#1D9E75' }}>{reason.trim().length}/{MIN_REASON} characters minimum</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="rounded-sm text-xs h-7">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={short || saving} data-testid="abr-override-btn"
            className="rounded-sm text-xs h-7" style={{ background: '#7C5CBF' }}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Record override'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ABRVerificationCard({ api, submissionId }) {
  const [gate, setGate] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    return api.get(`/submissions/${submissionId}/abr-verification`)
      .then(r => setGate(r.data))
      .catch(() => setGate(null))
      .finally(() => setLoading(false));
  }, [api, submissionId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !gate) return null;
  if (!gate || gate.status === 'not_applicable_nz') return null;

  const meta = META[gate.status] || { label: gate.status, color: '#5C6B7A' };
  const snap = gate.snapshot || {};
  const removeOverride = async () => {
    try {
      await api.delete(`/submissions/${submissionId}/abr-override`);
      toast.success('Override removed — gate re-blocks the bind');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove override');
    }
  };

  return (
    <Card className="rounded-md bg-white p-4" data-testid="abr-verification-card"
      style={{ border: '1px solid rgba(136,150,168,0.12)', borderLeft: `3px solid ${meta.color}` }}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          {gate.status === 'verified' ? <ShieldCheck className="w-4 h-4" style={{ color: meta.color }} />
            : <ShieldAlert className="w-4 h-4" style={{ color: meta.color }} />}
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#5C6B7A' }}>ABR Verification</h3>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold" data-testid="abr-status-chip"
            style={{ background: meta.color + '15', color: meta.color }}>{meta.label}</span>
        </div>
        <button onClick={load} data-testid="abr-refresh-btn" title="Re-check against the ABR"
          className="p-1 rounded hover:bg-slate-50" style={{ color: '#5C6B7A' }}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-[11px] font-mono mb-1" style={{ color: '#5C6B7A' }}>
        ABN {gate.abn || '—'} · insured “{gate.insured_name}”
        {!gate.configured && <span className="ml-2" style={{ color: '#E8A020' }}>· live ABR lookup activates when ABR_GUID is added</span>}
      </p>
      {gate.enforced === false && (
        <p className="text-[10px] px-2 py-1 rounded-sm mb-1" data-testid="abr-advisory-note"
          style={{ background: 'rgba(92,107,122,0.08)', color: '#5C6B7A', border: '1px dashed rgba(136,150,168,0.4)' }}>
          {gate.mode === 'off' ? 'NOT PART OF THE BIND PATH — ' : 'ADVISORY — '}{gate.advisory_note || 'ABR identity checks are recorded on the bind, not enforced (owner ruling, 10 Oct 2026).'}
        </p>
      )}
      {gate.advisories?.length > 0 && (
        <ul className="space-y-1 mt-1" data-testid="abr-advisories">
          {gate.advisories.map((r, i) => (
            <li key={i} className="text-xs" style={{ color: '#8A6D1F' }}>Recorded: {r}</li>
          ))}
        </ul>
      )}
      {gate.blocked_reasons?.length > 0 && (
        <ul className="space-y-1 mt-1" data-testid="abr-blocked-reasons">
          {gate.blocked_reasons.map((r, i) => (
            <li key={i} className="text-xs" style={{ color: '#B03230' }}>{r}</li>
          ))}
        </ul>
      )}
      {snap.entity_name && (
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]" data-testid="abr-snapshot">
          <span style={{ color: '#5C6B7A' }}>ABR legal name</span><span className="font-medium text-right" style={{ color: '#0D1B2A' }}>{snap.entity_name}</span>
          <span style={{ color: '#5C6B7A' }}>Entity type</span><span className="font-medium text-right" style={{ color: '#0D1B2A' }}>{snap.entity_type || '—'}</span>
          <span style={{ color: '#5C6B7A' }}>ABN status</span><span className="font-medium text-right" style={{ color: '#0D1B2A' }}>{snap.status || '—'}</span>
          <span style={{ color: '#5C6B7A' }}>GST registered</span><span className="font-medium text-right" style={{ color: '#0D1B2A' }}>{snap.gst_registered ? 'Yes' : 'No'}</span>
        </div>
      )}
      {gate.override && (
        <div className="mt-2 p-2 rounded-sm text-[11px]" data-testid="abr-override-info"
          style={{ background: 'rgba(124,92,191,0.06)', color: '#5C4A8A' }}>
          Override by <b>{gate.override.by_name}</b> ({gate.override.by_role}) on {new Date(gate.override.at).toLocaleDateString('en-AU')}: “{gate.override.reason}”
          <button onClick={removeOverride} data-testid="abr-override-remove"
            className="ml-2 underline" style={{ color: '#B03230' }}>Remove</button>
        </div>
      )}
      {gate.overridable && !gate.override && (
        <OverrideForm api={api} submissionId={submissionId} onDone={load} />
      )}
      {gate.status === 'verified_pending' && (
        <p className="text-[10px] mt-1" style={{ color: '#E8A020' }}>
          The ABR was unreachable — the bind proceeded flagged and the hourly worker re-verifies automatically.
        </p>
      )}
    </Card>
  );
}
